/**
 * One bus.
 *
 * Everything that happens in the world and might matter to somebody goes
 * through here: a thrown object, an impact, a noise, glass breaking, a cart
 * rolling, somebody helping themselves to another department's stock. Nothing
 * downstream knows what kind of object produced an event — a perception system
 * that has to ask whether the thing that just broke was a wine bottle or a
 * coffee cup is a perception system that needs editing every time an object is
 * added.
 *
 * Events are facts about the world, not requests. `emit` never returns a
 * decision and no handler can veto one. What a witness does about a noise is
 * the witness's business.
 */

export const EVENT = Object.freeze({
  /** Somebody let go of something, hard. */
  THROW: 'throw',
  /**
   * Somebody put something down.
   *
   * Its own type rather than a quiet throw or a noise with a label on it, and
   * the separation is the rule rather than tidiness. Putting down what you are
   * being watched holding is the counterplay to being watched holding it, so a
   * drop that arrives anywhere near the throw path is a counterplay that can
   * finish the accusation it exists to prevent. `suspicion.js` says what it is
   * worth and what it is never allowed to do.
   */
  DROP: 'drop',
  /** Something arrived somewhere at speed. */
  IMPACT: 'impact',
  /** A sound was made at a position, audible within a radius. */
  NOISE: 'noise',
  /** Something fragile stopped being one object. */
  BREAK: 'break',
  /** Something was taken from a place that was not yours. */
  TAKE: 'take',
  /** A body entered or left a zone the rules care about. */
  ZONE: 'zone',
  /** A consumable was used. */
  CONSUME: 'consume',
  /**
   * Somebody did something to somebody else for a laugh.
   *
   * Its own type for the same reason `DROP` is: a prank is witnessed exactly
   * like everything else, and what makes it different is not how it is seen
   * but what a witness may do with it. Keeping it off `TAKE` and off `THROW`
   * means the two pranks that can cost you something — a note somebody works
   * out, a caddy somebody watched you salt — can ask for a claim *about a
   * prank* rather than fishing through everything the player did today.
   */
  PRANK: 'prank',
});

export function createBus() {
  const byType = new Map();
  const all = new Set();
  /** The last few events, for debugging and for the panel. */
  let recent = [];

  return {
    on(type, handler) {
      let set = byType.get(type);
      if (!set) byType.set(type, (set = new Set()));
      set.add(handler);
      return () => set.delete(handler);
    },

    /** Every event, whatever it is. Perception subscribes this way. */
    onAny(handler) {
      all.add(handler);
      return () => all.delete(handler);
    },

    emit(type, payload = {}) {
      const event = { type, ...payload };
      for (const handler of byType.get(type) ?? []) handler(event);
      for (const handler of all) handler(event);
      recent = [...recent, event].slice(-40);
      return event;
    },

    get recent() {
      return recent;
    },

    clear() {
      recent = [];
    },
  };
}
